import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Stack,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
  Paper,
  Divider,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  QrCodeScanner,
  Delete,
  Storage,
  Upload,
  AddBoxOutlined,
  Autorenew,
  Save,
  Send,
  ArrowBack,
  LocalShipping,
  SmartToy,
  Info
} from '@mui/icons-material';
import { DataService } from '../../services/data-service';
import { LovService } from '../../services/lov-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import ExcelImportDialog, { ImportRow } from '../../components/ExcelImportDialog';
import VirtualizedMovementTable from '../../components/VirtualizedMovementTable';
import { BarcodeSearchResult, bulkBarcodeService } from '../../services/bulk-barcode-service';
import {
  GenerateBoxesDialog,
  EnhancedBoxScanDialog,
  TypeDetailsCell,
  AllPropertiesCell,
  BoxItem,
} from '../../components/MovementDialogs/SharedComponents';
import { usePrint } from '../../hooks/usePrint';
import { useNavigate } from 'react-router-dom';
import { useUserPersistentState } from '../../hooks/useUserPersistentState';
import { useTab } from '../../contexts/TabContext';

interface StorageUnit {
  id: string;
  name: string;
  code: string;
}

interface Box {
  boxBarcode: string;
  quantity: number;
}

interface InventoryLocation {
  storageUnitId: string;
  storageUnitName: string;
  storageUnitCode: string;
  boxes: Box[];
  independentQuantity?: number;
  totalQuantity: number;
  availableQuantity: number;
  itemName?: string;
  unitId?: string;
}

interface ShipmentItem {
  unitId: string;
  barcode: string;
  itemName: string;
  quantity: number;
  properties: Record<string, any>;
  sourceInventory?: {
    storageUnitId: string;
    storageUnitCode: string;
    storageUnitName: string;
    warehouseId: string;
    warehouseName: string;
  };
}

interface ShipmentBox {
  boxBarcode: string;
  totalItems: number;
  totalQuantity: number;
  commonProperties: Record<string, any>;
  recommendedStorageUnits: any[];
  items: ShipmentItem[];
  reasonIfNoRecommendation?: string;
}

interface ShipmentAnalysisResult {
  shipmentId: string;
  shipmentRefNumber: string;
  message: string;
  recommendations: ShipmentBox[];
  summary: {
    totalBoxes: number;
    boxesWithItems: number;
    boxesWithRecommendations: number;
    totalItems: number;
    totalQuantity: number;
  };
}

interface TransferRow {
  id: string;
  barcode: string;
  searchResult: BarcodeSearchResult | null;
  selectedSource?: {
    storageUnitId: string;
    storageUnitName: string;
    boxBarcode?: string;
    availableQuantity: number;
    availableBoxes?: { [key: string]: string };
  };
  selectedDestination?: string;
  selectedDestinationBoxRef?: string;
  quantity: number;
  loading: boolean;
  error?: string;
  unitData?: any;
  smartRecommended?: boolean;
  smartSourceSelected?: boolean;
  smartDestinationSelected?: boolean;
  smartRecommendations?: any[];
  shipmentOrigin?: boolean;
  shipmentBoxBarcode?: string;
  recommendedDestination?: string;
}

interface ReallocationData {
  name: string;
  refNumber: string;
  notes: string;
  referenceId: string;
  date: string; // Date-only format (YYYY-MM-DD)
}

type DestinationBoxes = Record<string, Record<string, string>>;

interface PersistentReallocationState {
  reallocationData: ReallocationData;
  transferRows: TransferRow[];
  destinationBoxes: DestinationBoxes;
  lastUpdated: number;
}

interface Shipment {
  id: string;
  refNumber: string;
  status: string;
  createdDate: string;
  notes?: string;
}

const normalizeBox = (b?: string | null) => (b === 'independent' ? '' : (b ?? ''));

const NewReallocationPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { printBoxLabel, formatBoxDataForPrinting } = usePrint();
  const { registerTabCloseCallback } = useTab();

  const defaultPersistentState: PersistentReallocationState = {
    reallocationData: {
      name: '',
      refNumber: '',
      notes: '',
      referenceId: '',
      date: new Date().toISOString().split('T')[0] // Default to today's date
    },
    transferRows: [
      { id: '1', barcode: '', searchResult: null, quantity: 1, loading: false }
    ],
    destinationBoxes: {},
    lastUpdated: Date.now()
  };

  const [persistentState, setPersistentState, clearPersistentState] = useUserPersistentState(
    'reallocation-form',
    defaultPersistentState,
    { debounceMs: 1000 }
  );

  const reallocationData = persistentState.reallocationData;
  const transferRows = persistentState.transferRows;
  const destinationBoxes = persistentState.destinationBoxes;

  const setReallocationData = (updater: (prev: ReallocationData) => ReallocationData | ReallocationData) => {
    setPersistentState(prev => ({
      ...prev,
      reallocationData: typeof updater === 'function' ? updater(prev.reallocationData) : updater,
      lastUpdated: Date.now()
    }));
  };

  const setTransferRows = (updater: (prev: TransferRow[]) => TransferRow[] | TransferRow[]) => {
    setPersistentState(prev => ({
      ...prev,
      transferRows: typeof updater === 'function' ? updater(prev.transferRows) : updater,
      lastUpdated: Date.now()
    }));
  };

  const setDestinationBoxes = (updater: (prev: DestinationBoxes) => DestinationBoxes | DestinationBoxes) => {
    setPersistentState(prev => ({
      ...prev,
      destinationBoxes: typeof updater === 'function' ? updater(prev.destinationBoxes) : updater,
      lastUpdated: Date.now()
    }));
  };

  const [destinationUnits, setDestinationUnits] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [destinationsLoaded, setDestinationsLoaded] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [shipmentDialogOpen, setShipmentDialogOpen] = useState(false);
  const [shipmentSearchText, setShipmentSearchText] = useState('');
  const [allShipments, setAllShipments] = useState<{id: string, refNumber: string}[]>([]);
  const [shipmentOptions, setShipmentOptions] = useState<{id: string, refNumber: string}[]>([]);
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<{id: string, refNumber: string} | null>(null);
  const [loadingShipmentAnalysis, setLoadingShipmentAnalysis] = useState(false);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [bulkDestination, setBulkDestination] = useState<string>('');
  const [bulkDestinationDialogOpen, setBulkDestinationDialogOpen] = useState(false);
  const [selectedBulkDestination, setSelectedBulkDestination] = useState<string>('');
  const [bulkSourceDialogOpen, setBulkSourceDialogOpen] = useState(false);
  const [selectedBulkSource, setSelectedBulkSource] = useState<string>('');
  const [generateBoxesDialog, setGenerateBoxesDialog] = useState(false);
  const [generatingBoxes, setGeneratingBoxes] = useState(false);
  const [boxScanDialog, setBoxScanDialog] = useState<{
    open: boolean;
    rowId: string;
    boxBarcode: string;
    boxDetails: any;
  }>({ open: false, rowId: '', boxBarcode: '', boxDetails: null });

  const [smartAllocateLoading, setSmartAllocateLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<any>(null);

  const statusCounts = useMemo(() => {
    const totalRows = transferRows.length;
    const withBarcodes = transferRows.filter(row => row.barcode.trim()).length;
    const withSources = transferRows.filter(row => row.barcode.trim() && row.selectedSource).length;
    const withDestinations = transferRows.filter(row => row.barcode.trim() && row.selectedDestination).length;
    const readyToMove = transferRows.filter(row => row.barcode.trim() && row.selectedSource && row.selectedDestination).length;
    const missingSources = transferRows.filter(row => row.barcode.trim() && !row.selectedSource).length;
    const missingDestinations = transferRows.filter(row => row.barcode.trim() && row.selectedSource && !row.selectedDestination).length;
    const errors = transferRows.filter(row => row.error).length;
    const boxes = transferRows.filter(row => row.searchResult?.type === 'box' && row.barcode.trim()).length;
    const units = transferRows.filter(row => row.searchResult?.type === 'unit' && row.barcode.trim()).length;
    const shipmentOrigin = transferRows.filter(row => row.shipmentOrigin && row.barcode.trim()).length;

    return {
      totalRows,
      withBarcodes,
      withSources,
      withDestinations,
      readyToMove,
      missingSources,
      missingDestinations,
      errors,
      boxes,
      units,
      shipmentOrigin,
      completion: withBarcodes > 0 ? Math.round((readyToMove / withBarcodes) * 100) : 0
    };
  }, [transferRows]);

  useEffect(() => {
    loadDestinations();
  }, []);

  useEffect(() => {
    const hasExistingData = persistentState.lastUpdated > 0 &&
      (persistentState.reallocationData.name ||
       persistentState.transferRows.some(row => row.barcode.trim()));

    if (hasExistingData) {
      const timeAgo = Math.round((Date.now() - persistentState.lastUpdated) / (1000 * 60)); // minutes
      const timeDisplay = timeAgo < 60
        ? `${timeAgo} minutes ago`
        : timeAgo < 1440
          ? `${Math.round(timeAgo / 60)} hours ago`
          : `${Math.round(timeAgo / 1440)} days ago`;

      dispatch(showAlert({
        message: `Restored your previous reallocation progress (saved ${timeDisplay}). You can continue where you left off.`,
        type: 'info'
      }));
    }
  }, []);

  // Register tab close cleanup callback
  useEffect(() => {
    registerTabCloseCallback('/movements/new-reallocation', () => {
      clearPersistentState();
    });
    // Note: We don't unregister on unmount - we want the callback to persist
    // so it runs when the tab is closed from the tab system
  }, [registerTabCloseCallback, clearPersistentState]);

  const loadDestinations = async () => {
    if (loadingDestinations || destinationsLoaded) return;

    setLoadingDestinations(true);
    try {
      const storageUnits = await LovService.getLovDictionary('leaf-storage-units');
      setDestinationUnits(storageUnits || {});
      setDestinationsLoaded(true);
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to load storage units',
        type: 'warning'
      }));
      setDestinationUnits({});
    } finally {
      setLoadingDestinations(false);
    }
  };

  const loadAllShipments = async () => {
    setLoadingShipments(true);
    try {
      const response = await DataService.get(`api/Lov/all-shipments`);

      if (!response.ok) {
        throw new Error('Failed to fetch shipments');
      }

      const result = await response.json();

      const transformedShipments = Object.entries(result as Record<string, string>)
        .map(([id, refNumber]) => ({ id, refNumber }));

      setAllShipments(transformedShipments);
      setShipmentOptions(transformedShipments);
    } catch (error: any) {
      console.warn('Failed to load shipments:', error);
      setAllShipments([]);
      setShipmentOptions([]);
    } finally {
      setLoadingShipments(false);
    }
  };

  const filterShipments = (searchText: string) => {
    if (!searchText.trim()) {
      setShipmentOptions(allShipments);
      return;
    }

    const filtered = allShipments.filter(option =>
      option.id.toLowerCase().includes(searchText.toLowerCase()) ||
      option.refNumber.toLowerCase().includes(searchText.toLowerCase())
    );

    setShipmentOptions(filtered);
  };

  const handleLoadShipment = async (shipment: {id: string, refNumber: string}) => {
    if (!shipment?.id) return;

    setLoadingShipmentAnalysis(true);
    try {
      const response = await DataService.post(`api/Shipment/${shipment.id}/smart-allocate-analyze`, {});
      
      if (!response.ok) {
        throw new Error('Failed to analyze shipment for reallocation');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Shipment analysis failed');
      }

      const analysisData: ShipmentAnalysisResult = result.data;
      
      const newTransferRows: TransferRow[] = [];
      let currentId = Date.now();

      for (const box of analysisData.recommendations) {
        const boxSearchResult: BarcodeSearchResult = {
          type: 'box',
          itemName: ``,
          unitId: box.boxBarcode,
          locations: [],
          unit: {
            boxItems: box.items.map(item => ({
              unitId: item.unitId,
              barcode: item.barcode,
              itemName: item.itemName,
              quantity: item.quantity,
              selectedSource: undefined
            }))
          }
        };

        const recommendedDestination = box.recommendedStorageUnits?.[0]?.storageUnitId || null;

        const sourceInventory = box.items[0]?.sourceInventory;

        const transferRow: TransferRow = {
          id: (currentId++).toString(),
          barcode: box.boxBarcode,
          searchResult: boxSearchResult,
          quantity: box.totalQuantity,
          loading: false,
          shipmentOrigin: true,
          shipmentBoxBarcode: box.boxBarcode,
          recommendedDestination,
          selectedSource: sourceInventory ? {
            storageUnitId: sourceInventory.storageUnitId,
            storageUnitName: sourceInventory.storageUnitName,
            availableQuantity: box.totalQuantity,
            availableBoxes: { [box.boxBarcode]: box.boxBarcode }
          } : undefined,
          selectedDestination: recommendedDestination,
          selectedDestinationBoxRef: recommendedDestination ? 'independent' : undefined,
          smartRecommended: !!recommendedDestination,
          smartDestinationSelected: !!recommendedDestination,
          smartSourceSelected: !!sourceInventory,
          smartRecommendations: box.recommendedStorageUnits || []
        };

        newTransferRows.push(transferRow);
      }

      setTransferRows(prev => {
        const existing = prev.filter(row => row.barcode.trim());
        return [
          ...existing,
          ...newTransferRows,
          { id: (Date.now() + newTransferRows.length).toString(), barcode: '', searchResult: null, quantity: 1, loading: false }
        ];
      });

      setReallocationData(prev => ({
        ...prev,
        name: prev.name || `Reallocation from ${analysisData.shipmentRefNumber}`,
        referenceId: prev.referenceId || shipment.id,
        notes: prev.notes || `Imported from shipment ${analysisData.shipmentRefNumber}`
      }));

      const uniqueDestinations = new Set(
        newTransferRows
          .map(row => row.recommendedDestination)
          .filter(Boolean)
      );

      for (const destinationId of uniqueDestinations) {
        try {
          if (!destinationBoxes[destinationId!]) {
            const response = await DataService.get(`api/Lov/${destinationId}/boxes`);
            if (response.ok) {
              const boxes = await response.json();
              setDestinationBoxes(prev => ({
                ...prev,
                [destinationId!]: boxes || {}
              }));
            }
          }
        } catch (error) {
          console.warn('Failed to load destination boxes:', destinationId);
        }
      }

      dispatch(showAlert({
        message: `Successfully loaded ${newTransferRows.length} boxes from shipment ${analysisData.shipmentRefNumber}. ${newTransferRows.filter(row => row.recommendedDestination).length} boxes have AI recommendations.`,
        type: 'success'
      }));

      setShipmentDialogOpen(false);
      setSelectedShipment(null);
      setShipmentSearchText('');

    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to load shipment for reallocation',
        type: 'error'
      }));
    } finally {
      setLoadingShipmentAnalysis(false);
    }
  };

  const searchBarcode = async (rowId: string, barcode: string) => {
    if (!barcode.trim()) return;

    const currentRow = transferRows.find(r => r.id === rowId);
    if (currentRow?.searchResult && currentRow.barcode === barcode) {
      return;
    }

    const existingRow = transferRows.find(r =>
      r.id !== rowId &&
      r.barcode.trim() === barcode.trim() &&
      r.selectedSource &&
      r.quantity >= (r.selectedSource.availableQuantity || 0)
    );

    if (existingRow) {
      dispatch(showAlert({
        message: `${barcode} already has maximum available quantity (${existingRow.selectedSource?.availableQuantity || 0}) in another row.`,
        type: 'warning'
      }));
      return;
    }

    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? { ...row, loading: true, error: undefined, searchResult: null, selectedSource: undefined, selectedDestination: undefined }
        : row
    ));

    try {
      const response = await DataService.get(`api/inventoryItem/search-barcode/${encodeURIComponent(barcode)}`);

      if (!response.ok) {
        throw new Error('Failed to search barcode');
      }

      const result = await response.json();

      if (!result?.success) {
        throw new Error(result?.message || 'Barcode not found');
      }

      const normalized = normalizeSingleSearch(result.data);
      if (!normalized) {
        const itemName = result.data?.itemName || barcode;

        setTransferRows(prev => prev.map(row =>
          row.id === rowId ? {
            ...row,
            loading: false,
            error: result.data?.itemName
              ? `${itemName} is known but not in inventory`
              : `${barcode} not found`,
            searchResult: null
          } : row
        ));

        dispatch(showAlert({
          message: result.data?.itemName
            ? `${itemName} is known but not in inventory. Cannot add to reallocation.`
            : `${barcode} not found. Cannot add to reallocation.`,
          type: 'error'
        }));
        return;
      }

      setTransferRows(prev => prev.map(row =>
        row.id === rowId
          ? { ...row, loading: false, barcode: barcode, searchResult: normalized, unitData: result.data }
          : row
      ));

      setTimeout(async () => {
        if (normalized.locations && normalized.locations.length === 1) {
          await handleSourceSelection(rowId, normalized.locations[0].storageUnitId);
        }
      }, 50);
    } catch (error: any) {
      setTransferRows(prev => {
        const filtered = prev.filter(row => row.id !== rowId);
        if (filtered.length === 0 || !filtered.some(row => !row.barcode?.trim())) {
          filtered.push({
            id: `empty-${Date.now()}`,
            barcode: '',
            searchResult: null,
            quantity: 1,
            loading: false
          });
        }
        return filtered;
      });

      dispatch(showAlert({
        message: `${barcode}: ${error.message || 'Search failed'}. Row removed.`,
        type: 'error'
      }));
    }
  };

  const handleBarcodeChange = (rowId: string, barcode: string) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId
        ? {
            ...row,
            barcode,
            ...(row.barcode !== barcode ? {
              searchResult: null,
              selectedSource: undefined,
              selectedDestination: undefined,
              error: undefined
            } : {})
          }
        : row
    ));
  };

  const handleBarcodeKeyPress = (event: React.KeyboardEvent, rowId: string) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = transferRows.find(r => r.id === rowId);
      if (!row || !row.barcode.trim()) return;

      if (row.searchResult && row.barcode.trim()) {
        focusOnSourceSelection(rowId);
        return;
      }

      searchBarcode(rowId, row.barcode);

      const currentIndex = transferRows.findIndex(r => r.id === rowId);
      if (currentIndex === transferRows.length - 1) {
        addNewRow();
      }
    }
  };

  const focusOnSourceSelection = (rowId: string) => {
    setTimeout(() => {
      const sourceSelect = document.querySelector(`[data-row-id="${rowId}"][data-field="source"]`) as HTMLElement;
      if (sourceSelect) {
        sourceSelect.focus();
      }
    }, 100);
  };

  const handleBarcodeSubmit = (rowId: string) => {
    const row = transferRows.find(r => r.id === rowId);
    if (!row || !row.barcode.trim()) return;

    if (row.searchResult && row.barcode.trim()) {
      return;
    }

    searchBarcode(rowId, row.barcode);

    const currentIndex = transferRows.findIndex(r => r.id === rowId);
    if (currentIndex === transferRows.length - 1) {
      addNewRow();
    }
  };

  const handleSourceSelection = async (rowId: string, storageUnitId: string, isFromSmartAllocation = false) => {
    const row = transferRows.find(r => r.id === rowId);
    if (!row || !row.searchResult) return;

    if (row.shipmentOrigin) {
      return;
    }

    const location = row.searchResult.locations.find(loc => loc.storageUnitId === storageUnitId);
    if (!location) return;

    const availableBoxes: { [key: string]: string } = {};

    if (location.boxes && location.boxes.length > 0) {
      location.boxes.forEach(box => {
        if (box.quantity > 0) {
          availableBoxes[box.boxBarcode] = box.boxBarcode;
        }
      });
    }

    if (location.independentQuantity && location.independentQuantity > 0) {
      availableBoxes['independent'] = 'Independent';
    }

    setTransferRows(prev => prev.map(r =>
      r.id === rowId
        ? {
            ...r,
            selectedSource: {
              storageUnitId: location.storageUnitId,
              storageUnitName: location.storageUnitName,
              boxBarcode: undefined,
              availableQuantity: location.totalQuantity || location.availableQuantity,
              availableBoxes
            },
            smartSourceSelected: isFromSmartAllocation ? r.smartSourceSelected : false
          }
        : r
    ));

    const boxEntries = Object.entries(availableBoxes);
    if (boxEntries.length === 1) {
      const [boxBarcode] = boxEntries[0];
      setTransferRows(prev => prev.map(r =>
        r.id === rowId && r.selectedSource
          ? {
              ...r,
              selectedSource: {
                ...r.selectedSource,
                boxBarcode: boxBarcode === 'independent' ? '' : boxBarcode
              }
            }
          : r
      ));
    }
  };

  const handleDestinationSelection = async (rowId: string, destinationId: string, isFromSmartAllocation = false) => {
    setTransferRows(prev => prev.map(r =>
      r.id === rowId
        ? {
            ...r,
            selectedDestination: destinationId,
            smartDestinationSelected: isFromSmartAllocation ? r.smartDestinationSelected : false
          }
        : r
    ));

    try {
      if (!destinationBoxes[destinationId]) {
        const response = await DataService.get(`api/Lov/${destinationId}/boxes`);
        if (response.ok) {
          const boxes = await response.json();
          setDestinationBoxes(prev => ({
            ...prev,
            [destinationId]: boxes || {}
          }));

          setTransferRows(prev => prev.map(r =>
            r.id === rowId
              ? { ...r, selectedDestinationBoxRef: 'independent' }
              : r
          ));
        }
      } else {
        setTransferRows(prev => prev.map(r =>
          r.id === rowId
            ? { ...r, selectedDestinationBoxRef: 'independent' }
            : r
        ));
      }
    } catch (error) {
      console.warn('Failed to load destination boxes:', destinationId);
      setTransferRows(prev => prev.map(r =>
        r.id === rowId
          ? { ...r, selectedDestinationBoxRef: 'independent' }
          : r
      ));
    }
  };

  const handleSourceBoxSelection = (rowId: string, boxBarcode: string) => {
    const row = transferRows.find(r => r.id === rowId);
    if (!row?.selectedSource || !row.searchResult) return;

    if (row.shipmentOrigin) {
      return;
    }

    const location = row.searchResult.locations.find(loc => loc.storageUnitId === row.selectedSource!.storageUnitId);
    if (!location) return;

    let availableQuantity = 0;
    if (boxBarcode === 'independent') {
      availableQuantity = location.independentQuantity || 0;
    } else {
      const selectedBox = location.boxes?.find(box => box.boxBarcode === boxBarcode);
      availableQuantity = selectedBox?.quantity || 0;
    }

    setTransferRows(prev => prev.map(r =>
      r.id === rowId && r.selectedSource
        ? {
            ...r,
            selectedSource: {
              ...r.selectedSource,
              boxBarcode: boxBarcode === 'independent' ? '' : boxBarcode,
              availableQuantity
            },
            quantity: r.quantity > availableQuantity ? Math.min(r.quantity, availableQuantity) : r.quantity
          }
        : r
    ));
  };

  const handleDestinationBoxSelection = (rowId: string, boxRefNumber: string) => {
    setTransferRows(prev => prev.map(r =>
      r.id === rowId
        ? { ...r, selectedDestinationBoxRef: boxRefNumber }
        : r
    ));
  };

  const addNewRow = () => {
    const newId = Date.now().toString();
    setTransferRows(prev => [...prev, {
      id: newId,
      barcode: '',
      searchResult: null,
      quantity: 1,
      loading: false
    }]);
  };

  const removeRow = (rowId: string) => {
    if (transferRows.length === 1) return;
    setTransferRows(prev => prev.filter(row => row.id !== rowId));
  };

  const updateRowField = (rowId: string, field: keyof TransferRow, value: any) => {
    setTransferRows(prev => prev.map(row =>
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  const handleRowSelection = (rowId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSelection = new Set(prev);
      if (selected) {
        newSelection.add(rowId);
      } else {
        newSelection.delete(rowId);
      }
      return newSelection;
    });
  };

  const filterStockedLocations = <T extends { totalQuantity?: number }>(locs?: T[]) =>
    (locs || []).filter(l => (l.totalQuantity ?? 0) > 0);

  const normalizeSingleSearch = (data: any): any | null => {
    if (!data) return null;
    const locs = filterStockedLocations(data.locations);
    if (!locs.length) return null;
    return {
      ...data,
      locations: locs
    };
  };

  const applySmartAllocationRecommendations = async (smartData: any) => {
    if (!smartData?.barcodeAllocations || !Array.isArray(smartData.barcodeAllocations)) {
      return;
    }

    let appliedCount = 0;
    let selectedSourcesCount = 0;
    let selectedDestinationsCount = 0;

    const rowUpdates: Record<string, any> = {};

    for (const allocation of smartData.barcodeAllocations) {
      if (!allocation.success || !allocation.barcode) continue;

      const rowToUpdate = transferRows.find(row =>
        row.barcode.trim() === allocation.barcode.trim()
      );

      if (!rowToUpdate) continue;

      try {
        let selectedSource = rowToUpdate.selectedSource;
        let selectedDestination = rowToUpdate.selectedDestination;
        let hasSource = false;
        let hasDestination = false;

        if (allocation.itemProperties?.warehouseLocations?.length > 0) {
          const sourceLocation = allocation.itemProperties.warehouseLocations[0];
          if (sourceLocation.storageUnitIds?.length > 0) {
            const storageUnitId = sourceLocation.storageUnitIds[0];

            const location = rowToUpdate.searchResult?.locations?.find(loc =>
              loc.storageUnitId === storageUnitId
            );

            if (location) {
              const availableBoxes: { [key: string]: string } = {};

              if (location.boxes && location.boxes.length > 0) {
                location.boxes.forEach(box => {
                  if (box.quantity > 0) {
                    availableBoxes[box.boxBarcode] = box.boxBarcode;
                  }
                });
              }

              if (location.independentQuantity && location.independentQuantity > 0) {
                availableBoxes['independent'] = 'Independent';
              }

              selectedSource = {
                storageUnitId: location.storageUnitId,
                storageUnitName: location.storageUnitName,
                boxBarcode: undefined,
                availableQuantity: location.totalQuantity || location.availableQuantity,
                availableBoxes
              };

              const boxEntries = Object.entries(availableBoxes);
              if (boxEntries.length === 1) {
                const [boxBarcode] = boxEntries[0];
                selectedSource.boxBarcode = boxBarcode === 'independent' ? '' : boxBarcode;
              }

              selectedSourcesCount++;
              hasSource = true;
            }
          }
        }

        if (allocation.recommendations?.length > 0) {
          const sortedRecommendations = [...allocation.recommendations].sort(
            (a: any, b: any) => {
              const scoreA = a.combinedScore || 0;
              const scoreB = b.combinedScore || 0;
              if (scoreA === scoreB) return 0;
              return scoreB - scoreA;
            }
          );

          const bestRecommendation = sortedRecommendations[0];
          if (bestRecommendation.storageUnitId) {
            selectedDestination = bestRecommendation.storageUnitId;
            selectedDestinationsCount++;
            hasDestination = true;
          }
        }

        rowUpdates[rowToUpdate.id] = {
          selectedSource,
          selectedDestination,
          selectedDestinationBoxRef: hasDestination ? 'independent' : rowToUpdate.selectedDestinationBoxRef,
          smartRecommended: true,
          smartSourceSelected: hasSource,
          smartDestinationSelected: hasDestination,
          smartRecommendations: allocation.recommendations || []
        };

        appliedCount++;
      } catch (error) {
        console.warn(`Failed to apply smart recommendation for ${allocation.barcode}:`, error);
      }
    }

    setTransferRows(prev => prev.map(row => ({
      ...row,
      smartRecommended: false,
      smartSourceSelected: false,
      smartDestinationSelected: false,
      smartRecommendations: [],
      ...(rowUpdates[row.id] || {})
    })));

    for (const [rowId, updates] of Object.entries(rowUpdates)) {
      if (updates.selectedDestination && updates.smartDestinationSelected) {
        try {
          const destinationId = updates.selectedDestination;
          if (!destinationBoxes[destinationId]) {
            const response = await DataService.get(`api/Lov/${destinationId}/boxes`);
            if (response.ok) {
              const boxes = await response.json();
              setDestinationBoxes(prev => ({
                ...prev,
                [destinationId]: boxes || {}
              }));
            }
          }
        } catch (error) {
          console.warn('Failed to load destination boxes:', updates.selectedDestination);
        }
      }
    }

    if (appliedCount > 0) {
      dispatch(showAlert({
        message: `Applied smart recommendations: ${selectedSourcesCount} sources and ${selectedDestinationsCount} destinations auto-selected for ${appliedCount} items.`,
        type: 'success'
      }));
    }
  };

  const handleSmartAllocate = async () => {
    const validRowsForAllocation = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.error && !row.shipmentOrigin // Exclude shipment items from smart allocation
    );

    if (validRowsForAllocation.length === 0) {
      dispatch(showAlert({
        message: 'No valid items to analyze for smart allocation',
        type: 'warning'
      }));
      return;
    }

    setSmartAllocateLoading(true);
    try {
      const items = validRowsForAllocation.map(row => ({
        barcode: row.barcode,
        quantity: row.quantity
      }));

      const payload = {
        items,
        direction: 0,
        optimizeForProximity: true,
        maxRecommendations: 3,
        preferSameParent: true
      };

      const response = await DataService.post('api/InventoryAllocation/analyze', payload);

      if (!response.ok) {
        throw new Error('Failed to analyze inventory allocation');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Smart allocation analysis failed');
      }

      setRecommendations(result.data);

      await applySmartAllocationRecommendations(result.data);

      dispatch(showAlert({
        message: `Smart allocation completed. Found recommendations for ${result.data?.barcodeAllocations?.length || 0} items.`,
        type: 'success'
      }));

    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to perform smart allocation',
        type: 'error'
      }));
    } finally {
      setSmartAllocateLoading(false);
    }
  };

  const handleImport = async (importRows: ImportRow[]) => {
    if (!importRows.length) return;

    try {
      const qtyByBarcode = new Map<string, number>();
      for (const row of importRows) {
        const b = (row.barcode || '').trim();
        if (!b) continue;
        const q = Number(row.quantity) || 0;
        qtyByBarcode.set(b, (qtyByBarcode.get(b) || 0) + q);
      }
      const barcodes = [...qtyByBarcode.keys()];
      if (barcodes.length === 0) return;

      const results = await bulkBarcodeService.searchBarcodes(barcodes, {
        includeLocations: true,
        includeBoxes: true,
      });

      const foundItems: TransferRow[] = [];
      let ignoredUnknown = 0;
      let ignoredNoInventory = 0;

      let currentId = Date.now();

      for (const r of results) {
        const qty = qtyByBarcode.get(r.barcode) ?? 0;

        if (!r.found || !r.locations || r.locations.length === 0) {
          if (r.itemName) ignoredNoInventory++;
          else ignoredUnknown++;
          continue;
        }

        const searchResult: BarcodeSearchResult = {
          type: r.type === 'box' ? 'box' : 'unit',
          itemName: r.itemName || '',
          unitId: r.unitId,
          locations: r.locations,
          unit: r.unit
        };

        foundItems.push({
          id: (currentId++).toString(),
          barcode: r.barcode,
          searchResult,
          quantity: Math.max(1, qty || 1),
          loading: false,
          unitData: r.unit
        });
      }

      setTransferRows(prev => {
        const existing = prev.filter(row => row.barcode.trim());
        return [
          ...existing,
          ...foundItems,
          {
            id: (Date.now() + foundItems.length).toString(),
            barcode: '',
            searchResult: null,
            quantity: 1,
            loading: false,
          },
        ];
      });

      const importedCount = foundItems.length;
      const ignoredTotal = ignoredUnknown + ignoredNoInventory;
      const msg =
        importedCount > 0
          ? ignoredTotal > 0
            ? `Imported ${importedCount} item(s). ${ignoredTotal} unavailable item(s) ignored.`
            : `Imported ${importedCount} item(s).`
          : `No items imported. ${ignoredTotal} unavailable item(s) ignored.`;

      dispatch(showAlert({
        message: msg,
        type: importedCount > 0 ? 'success' : 'warning',
      }));
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to import items',
        type: 'error',
      }));
      throw error;
    }
  };

  const handleSelectAllWithNoSource = () => {
    const rowsWithNoSource = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && !row.selectedSource && !row.shipmentOrigin
    ).map(row => row.id);
    setSelectedRows(new Set(rowsWithNoSource));
  };

  const handleSelectAllWithNoDestination = () => {
    const rowsWithNoDestination = transferRows.filter(row =>
      row.barcode.trim() && row.searchResult && row.selectedSource && !row.selectedDestination
    ).map(row => row.id);
    setSelectedRows(new Set(rowsWithNoDestination));
  };

  const handleBulkDestinationApply = (destinationId: string) => {
    if (!destinationId || selectedRows.size === 0) return;

    let appliedCount = 0;
    selectedRows.forEach(rowId => {
      const row = transferRows.find(r => r.id === rowId);
      if (row && row.searchResult && row.selectedSource) {
        handleDestinationSelection(rowId, destinationId);
        appliedCount++;
      }
    });

    dispatch(showAlert({
      message: `Applied destination to ${appliedCount} of ${selectedRows.size} selected items`,
      type: appliedCount > 0 ? 'success' : 'warning'
    }));

    if (appliedCount > 0) {
      setSelectedRows(new Set());
    }
  };

  const handleBulkSourceApply = async (sourceId: string) => {
    if (!sourceId || selectedRows.size === 0) return;

    let appliedCount = 0;
    for (const rowId of selectedRows) {
      const row = transferRows.find(r => r.id === rowId);
      if (row && row.searchResult && !row.selectedSource && !row.shipmentOrigin) {
        await handleSourceSelection(rowId, sourceId);
        appliedCount++;
      }
    }

    dispatch(showAlert({
      message: `Applied source to ${appliedCount} of ${selectedRows.size} selected items`,
      type: appliedCount > 0 ? 'success' : 'warning'
    }));

    if (appliedCount > 0) {
      setSelectedRows(new Set());
    }
  };

  const renderItemsToTransfer = () => (
    <Stack spacing={2}>
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: '100%'}}>
        <VirtualizedMovementTable
          data={transferRows}
          itemsPerPage={15}
          height="auto"
          showPagination={true}
          emptyMessage="Start scanning barcodes to add items for reallocation, or import from shipment"
          dense={true}
          rowHeight="compact"
          ensureEmptyRow={true}
          onAddNewRow={addNewRow}
          columns={[
            {
              field: 'select',
              headerName: '☐',
              width: 50,
              renderCell: (row: TransferRow) => {
                const isSelectable = row.barcode.trim() && row.searchResult;
                return (
                  <Checkbox
                    checked={selectedRows.has(row.id)}
                    onChange={(e) => handleRowSelection(row.id, e.target.checked)}
                    disabled={!isSelectable || submitting}
                    size="small"
                  />
                );
              }
            },
            {
              field: 'barcode',
              headerName: 'Barcode',
              width: 140,
              renderCell: (row: TransferRow) => {
                if (row.shipmentOrigin) {
                  return (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <LocalShipping sx={{ color: 'primary.main', fontSize: '1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {row.barcode}
                      </Typography>
                    </Stack>
                  );
                }

                return (
                  <TextField
                    value={row.barcode || ''}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleBarcodeChange(row.id, e.target.value);
                    }}
                    onKeyDown={(e) => handleBarcodeKeyPress(e, row.id)}
                    onBlur={() => handleBarcodeSubmit(row.id)}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="Scan barcode"
                    size="small"
                    fullWidth
                    disabled={submitting}
                    autoComplete="off"
                    InputProps={{
                      startAdornment: <QrCodeScanner sx={{ mr: 0.5, color: 'action.active', fontSize: '1rem' }} />,
                      endAdornment: row.loading && <CircularProgress size={16} />
                    }}
                    error={!!row.error}
                    sx={{ minWidth: 140, '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }}
                  />
                );
              }
            },
            {
              field: 'type',
              headerName: 'Details',
              width: 220,
              renderCell: (row: TransferRow) => {
                if (row.shipmentOrigin && row.searchResult) {
                  const boxItems = row.searchResult?.unit?.boxItems || [];
                  const recommendation = row.smartRecommendations?.[0];

                  // Support both API formats
                  const reasonsList = recommendation?.matchedProperties || recommendation?.reasons || [];
                  const score = recommendation?.combinedScore || recommendation?.compatibilityScore || recommendation?.matchScore;

                  const tooltipContent = reasonsList.length > 0 ? (
                    <Stack spacing={0.5} sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                        AI Recommendation:
                      </Typography>
                      {reasonsList.map((reason: string, idx: number) => (
                        <Typography key={idx} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                          • {reason}
                        </Typography>
                      ))}
                      {score !== undefined && (
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                          Match Score: {Math.round(score)} pts
                        </Typography>
                      )}
                    </Stack>
                  ) : 'AI recommendation available';

                  return (
                    <Stack direction="column" spacing={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalShipping sx={{ color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                          Shipment Box
                        </Typography>
                        {row.recommendedDestination && (
                          <Tooltip title={tooltipContent} arrow placement="right">
                            <Stack direction="row" alignItems="center" spacing={0.25}>
                              <SmartToy sx={{ color: 'secondary.main', fontSize: '0.75rem' }} />
                              <Info sx={{ color: 'secondary.main', fontSize: '0.65rem' }} />
                            </Stack>
                          </Tooltip>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {boxItems.length} items, {row.quantity} total qty
                      </Typography>
                      {row.searchResult.itemName && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {row.searchResult.itemName}
                        </Typography>
                      )}
                    </Stack>
                  );
                }
                
                const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');
                if (isGeneratedBox && row.searchResult) {
                  const boxItems = row.searchResult?.unit?.boxItems || [];
                  const recommendation = row.smartRecommendations?.[0];

                  // Support both API formats
                  const reasonsList = recommendation?.matchedProperties || recommendation?.reasons || [];
                  const score = recommendation?.combinedScore || recommendation?.compatibilityScore || recommendation?.matchScore;

                  const tooltipContent = reasonsList.length > 0 ? (
                    <Stack spacing={0.5} sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                        Recommendation:
                      </Typography>
                      {reasonsList.map((reason: string, idx: number) => (
                        <Typography key={idx} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                          • {reason}
                        </Typography>
                      ))}
                      {score !== undefined && (
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                          Match Score: {Math.round(score)} pts
                        </Typography>
                      )}
                    </Stack>
                  ) : null;

                  return (
                    <Stack direction="column" spacing={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <AddBoxOutlined sx={{ color: 'secondary.main', fontSize: '0.875rem' }} />
                        <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'secondary.main' }}>
                          Generated Box
                        </Typography>
                        {row.smartRecommended && tooltipContent && (
                          <Tooltip title={tooltipContent} arrow placement="right">
                            <Stack direction="row" alignItems="center" spacing={0.25}>
                              <SmartToy sx={{ color: 'secondary.main', fontSize: '0.75rem' }} />
                              <Info sx={{ color: 'secondary.main', fontSize: '0.65rem' }} />
                            </Stack>
                          </Tooltip>
                        )}
                      </Stack>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', mt: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const boxDetails = {
                            boxBarcode: row.barcode,
                            items: boxItems,
                            totalItems: boxItems.length,
                            totalQuantity: boxItems.reduce((sum, item) => sum + item.quantity, 0)
                          };
                          setBoxScanDialog({
                            open: true,
                            rowId: row.id,
                            boxBarcode: row.barcode,
                            boxDetails
                          });
                        }}
                      >
                        See Box ({boxItems.length} items)
                      </Button>
                    </Stack>
                  );
                }

                // Regular item with optional recommendation info
                const recommendation = row.smartRecommendations?.[0];

                // Support both API formats
                const reasonsList = recommendation?.matchedProperties || recommendation?.reasons || [];
                const score = recommendation?.combinedScore || recommendation?.compatibilityScore || recommendation?.matchScore;

                const tooltipContent = reasonsList.length > 0 ? (
                  <Stack spacing={0.5} sx={{ p: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                      Recommendation:
                    </Typography>
                    {reasonsList.map((reason: string, idx: number) => (
                      <Typography key={idx} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                        • {reason}
                      </Typography>
                    ))}
                    {score !== undefined && (
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                        Match Score: {Math.round(score)} pts
                      </Typography>
                    )}
                  </Stack>
                ) : null;

                return (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Box sx={{ flex: 1 }}>
                      <AllPropertiesCell row={row} />
                    </Box>
                    {row.smartRecommended && tooltipContent && (
                      <Tooltip title={tooltipContent} arrow placement="right">
                        <Stack direction="row" alignItems="center" spacing={0.25}>
                          <SmartToy sx={{ color: 'secondary.main', fontSize: '0.75rem' }} />
                          <Info sx={{ color: 'secondary.main', fontSize: '0.65rem' }} />
                        </Stack>
                      </Tooltip>
                    )}
                  </Stack>
                );
              }
            },
            {
              field: 'source',
              headerName: 'Source',
              width: 150,
              renderCell: (row: TransferRow) => {
                if (row.shipmentOrigin) {
                  return (
                    <Stack direction="column" spacing={0.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalShipping sx={{ color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                          Shipment
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {row.selectedSource?.storageUnitName || 'From Shipment'}
                      </Typography>
                    </Stack>
                  );
                }

                if (row.searchResult) {
                  const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');

                  if (isGeneratedBox) {
                    if (row.selectedSource) {
                      return (
                        <Stack direction="column" spacing={0.5}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                            {row.selectedSource.storageUnitName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                            Generated Box Source
                          </Typography>
                        </Stack>
                      );
                    }

                    return (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Generated Box (No Source)
                      </Typography>
                    );
                  }

                  return (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontSize: '0.8rem' }}>Source</InputLabel>
                        <Select
                          value={row.selectedSource?.storageUnitId || ''}
                          onChange={(e) => handleSourceSelection(row.id, e.target.value)}
                          label="Source"
                          disabled={submitting}
                          sx={{
                            minWidth: 120,
                            fontSize: '0.8rem',
                            ...(row.smartSourceSelected && {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1976d2',
                                borderWidth: '2px'
                              }
                            })
                          }}
                          renderValue={(selected) => {
                            if (!selected) return '';
                            const location = row.searchResult?.locations?.find(loc => loc.storageUnitId === selected);
                            return location?.storageUnitName || selected;
                          }}
                        >
                          {row.searchResult.locations.map((location) => {
                            // Check if this location has a recommendation score
                            const recommendation = row.smartRecommendations?.find(rec =>
                              rec.storageUnitId === location.storageUnitId
                            );

                            // Support both API formats
                            const reasonsList = recommendation?.matchedProperties || recommendation?.reasons || [];
                            const score = recommendation?.combinedScore || recommendation?.compatibilityScore || recommendation?.matchScore || 0;
                            const scoreColor = score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error';

                            const tooltipContent = reasonsList.length > 0 ? (
                              <Stack spacing={0.5} sx={{ p: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                                  Why Recommended:
                                </Typography>
                                {reasonsList.map((reason: string, idx: number) => (
                                  <Typography key={idx} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                                    • {reason}
                                  </Typography>
                                ))}
                                {score !== undefined && (
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                                    Match Score: {Math.round(score)} pts
                                  </Typography>
                                )}
                              </Stack>
                            ) : '';

                            return (
                              <MenuItem key={location.storageUnitId} value={location.storageUnitId} sx={{ fontSize: '0.8rem' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: recommendation ? '100px' : '140px'
                                    }}
                                    title={`${location.storageUnitName} (Available: ${location.totalQuantity})`}
                                  >
                                    {location.storageUnitName} ({location.totalQuantity})
                                  </Typography>
                                  {recommendation && (
                                    <Tooltip title={tooltipContent} arrow placement="right">
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Chip
                                          label={`${Math.round(score)} pts`}
                                          size="small"
                                          color={scoreColor}
                                          variant="outlined"
                                          sx={{
                                            fontSize: '0.55rem',
                                            height: 14,
                                            minWidth: 32,
                                            '& .MuiChip-label': { px: 0.5 }
                                          }}
                                        />
                                        <Info sx={{ fontSize: '0.75rem', color: 'primary.main' }} />
                                      </Stack>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Stack>
                  );
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
              }
            },
            {
              field: 'sourceBox',
              headerName: 'Source Box',
              width: 120,
              renderCell: (row: TransferRow) => {
                // For shipment origin items, show shipment box
                if (row.shipmentOrigin) {
                  return (
                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                      {row.shipmentBoxBarcode || 'Shipment Box'}
                    </Typography>
                  );
                }

                const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');

                if (isGeneratedBox) {
                  if (row.selectedSource?.boxBarcode) {
                    return (
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                        {row.selectedSource.boxBarcode === 'independent' ? 'Independent' : row.selectedSource.boxBarcode}
                      </Typography>
                    );
                  }

                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      N/A (Generated)
                    </Typography>
                  );
                }

                if (row.selectedSource?.availableBoxes) {
                  const boxEntries = Object.entries(row.selectedSource.availableBoxes);
                  if (boxEntries.length <= 1) {
                    const displayText = boxEntries.length === 1 ?
                      (boxEntries[0][0] === 'independent' ? 'Independent' : boxEntries[0][0]) :
                      'Auto';
                    return (
                      <Typography variant="caption" color="text.secondary">
                        {displayText}
                      </Typography>
                    );
                  }
                  return (
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Box</InputLabel>
                      <Select
                        value={row.selectedSource.boxBarcode || ''}
                        onChange={(e) => handleSourceBoxSelection(row.id, e.target.value)}
                        label="Box"
                        disabled={submitting}
                        sx={{ minWidth: 100, fontSize: '0.75rem' }}
                      >
                       {boxEntries.map(([boxBarcode, _boxRef]) => (
                          <MenuItem key={boxBarcode || 'independent'} value={boxBarcode}>
                            <Typography variant="caption">
                              {boxBarcode === '' ? 'Independent' : boxBarcode}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
              }
            },
            {
              field: 'quantity',
              headerName: 'Qty',
              width: 70,
              renderCell: (row: TransferRow) => (
                <TextField
                  type="number"
                  value={row.quantity}
                  onChange={(e) => {
                    const newQuantity = parseInt(e.target.value) || 1;
                    setTransferRows(prev => prev.map(r =>
                      r.id === row.id
                        ? { ...r, quantity: newQuantity }
                        : r
                    ));
                  }}
                  size="small"
                  inputProps={{
                    min: 1,
                    max: row.selectedSource?.availableQuantity || 999,
                    style: { width: 60 }
                  }}
                  disabled={submitting || !row.searchResult || (row.shipmentOrigin && !row.searchResult?.unit?.boxItems)}
                  error={!!row.selectedSource && row.quantity > row.selectedSource.availableQuantity}
                />
              )
            },
            {
              field: 'destination',
              headerName: 'Destination',
              width: 150,
              renderCell: (row: TransferRow) => {
                const availableDestinations = Object.entries(destinationUnits)
                  .filter(([id]) => id !== row.selectedSource?.storageUnitId);

                return (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Destination</InputLabel>
                      <Select
                        value={row.selectedDestination || ''}
                        onChange={(e) => handleDestinationSelection(row.id, String(e.target.value))}
                        label="Destination"
                        disabled={submitting || !row.searchResult || loadingDestinations}
                        sx={{
                          minWidth: 140,
                          fontSize: '0.8rem',
                          ...(row.smartDestinationSelected && {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#9c27b0',
                              borderWidth: '2px'
                            }
                          }),
                          // Highlight AI recommended destination
                          ...(row.recommendedDestination === row.selectedDestination && {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#ff9800',
                              borderWidth: '2px'
                            }
                          })
                        }}
                        renderValue={(selected) => {
                          if (!selected) return '';
                          const name = destinationUnits[selected] || selected;
                          const isRecommended = row.recommendedDestination === selected;
                          return (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {isRecommended && <SmartToy sx={{ fontSize: '0.75rem', color: 'orange' }} />}
                              <span>{name}</span>
                            </Stack>
                          );
                        }}
                      >
                        {/* Recommended destinations section */}
                        {row.smartRecommendations && row.smartRecommendations.length > 0 && [
                          <MenuItem key="recommended-header" disabled sx={{ opacity: '1 !important' }}>
                            <Typography variant="caption" color="primary" fontWeight="600">
                              🎯 RECOMMENDED
                            </Typography>
                          </MenuItem>,
                          ...row.smartRecommendations.map((rec: any) => {
                            const isSelected = row.selectedDestination === rec.storageUnitId;
                            const destName = destinationUnits[rec.storageUnitId] || rec.storageUnitName || rec.storageUnitCode;

                            // Support both API formats: smart allocation (combinedScore/compatibilityScore/reasons)
                            // and shipment import (matchScore/matchedProperties)
                            const score = rec.combinedScore || rec.compatibilityScore || rec.matchScore || 0;
                            const scoreColor = score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error';

                            // Use matchedProperties for shipment import, reasons for smart allocation
                            const reasonsList = rec.matchedProperties || rec.reasons || [];

                            const tooltipContent = reasonsList.length > 0 ? (
                              <Stack spacing={0.5} sx={{ p: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white' }}>
                                  Why Recommended:
                                </Typography>
                                {reasonsList.map((reason: string, idx: number) => (
                                  <Typography key={idx} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                                    • {reason}
                                  </Typography>
                                ))}
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                                  Match Score: {Math.round(score)} pts
                                </Typography>
                                {rec.storageProperties && Object.keys(rec.storageProperties).length > 0 && (
                                  <>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'white', mt: 0.5 }}>
                                      Storage Properties:
                                    </Typography>
                                    {Object.entries(rec.storageProperties).map(([key, value]: [string, any]) => (
                                      <Typography key={key} variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>
                                        • {key}: {String(value)}
                                      </Typography>
                                    ))}
                                  </>
                                )}
                              </Stack>
                            ) : '';

                            return (
                              <MenuItem key={rec.storageUnitId} value={rec.storageUnitId}>
                                <Box sx={{ width: '100%' }}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                    <Typography variant="body2" fontWeight="500" sx={{ flex: 1 }}>
                                      {destName}
                                    </Typography>
                                    <Tooltip title={tooltipContent} arrow placement="right">
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Chip
                                          size="small"
                                          label={`${Math.round(score)} pts`}
                                          color={scoreColor}
                                        />
                                        <Info sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                                      </Stack>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </MenuItem>
                            );
                          }),
                          <Divider key="divider" />
                        ]}

                        {/* Other destinations section */}
                        {availableDestinations
                          .filter(([id]) => !row.smartRecommendations?.some((rec: any) => rec.storageUnitId === id))
                          .length > 0 && [
                          <MenuItem key="other-header" disabled sx={{ opacity: '1 !important' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                              📦 OTHER UNITS
                            </Typography>
                          </MenuItem>,
                          ...availableDestinations
                            .filter(([id]) => !row.smartRecommendations?.some((rec: any) => rec.storageUnitId === id))
                            .map(([id, name]) => (
                              <MenuItem key={id} value={id}>
                                <Typography variant="body2">
                                  {name}
                                </Typography>
                              </MenuItem>
                            ))
                        ]}
                      </Select>
                    </FormControl>
                  </Stack>
                );
              }
            },
            {
              field: 'destinationBox',
              headerName: 'Dest Box',
              width: 120,
              renderCell: (row: TransferRow) => {
                if (row.searchResult?.type === 'box') {
                  return (
                    <Typography variant="caption" color="text.secondary">
                      N/A (Box)
                    </Typography>
                  );
                }

                if (row.selectedDestination) {
                  const availableDestBoxes = destinationBoxes[row.selectedDestination] || {};
                  const boxEntries = Object.entries(availableDestBoxes);

                  return (
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Box Ref</InputLabel>
                      <Select<string>
                        value={row.selectedDestinationBoxRef || 'independent'}
                        onChange={(e) => handleDestinationBoxSelection(row.id, String(e.target.value))}
                        label="Box Ref"
                        disabled={submitting}
                        sx={{ minWidth: 100, fontSize: '0.75rem' }}
                      >
                        <MenuItem value="independent">
                          <Typography variant="caption">📦 Loose (Independent)</Typography>
                        </MenuItem>
                        {boxEntries.length > 0 && (
                          <MenuItem disabled>
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              — Available Boxes —
                            </Typography>
                          </MenuItem>
                        )}
                        {boxEntries.map(([boxBarcode, boxRefNumber]) => (
                          <MenuItem key={boxBarcode} value={String(boxRefNumber)}>
                            <Typography variant="caption">📦 {boxRefNumber}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return <Typography variant="body2" color="text.secondary">-</Typography>;
              }
            },
            {
              field: 'actions',
              headerName: 'Actions',
              width: 80,
              renderCell: (row: TransferRow) => (
                <Stack direction="row" spacing={0.5}>
                  {transferRows.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeRow(row.id)}
                      disabled={submitting}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              )
            }
          ]}
        />
      </Box>
    </Stack>
  );

  const validateReallocation = (): string[] => {
    const errors: string[] = [];

    if (!reallocationData.name.trim()) {
      errors.push('Movement name is required');
    }

    const validRows = transferRows.filter(row => row.barcode.trim() && row.searchResult);
    if (validRows.length === 0) {
      errors.push('At least one item must be scanned');
    }

    validRows.forEach((row, index) => {
      const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');
      const isShipmentOrigin = row.shipmentOrigin;

      if (!isGeneratedBox && !isShipmentOrigin && !row.selectedSource) {
        errors.push(`Row ${index + 1}: Source storage unit not selected`);
      }
      if (!row.selectedDestination) {
        errors.push(`Row ${index + 1}: Destination storage unit not selected`);
      }
      if (row.quantity <= 0) {
        errors.push(`Row ${index + 1}: Quantity must be greater than 0`);
      }
      if (!isGeneratedBox && !isShipmentOrigin && row.selectedSource?.availableQuantity && row.quantity > row.selectedSource.availableQuantity) {
        errors.push(`Row ${index + 1}: Quantity (${row.quantity}) exceeds available (${row.selectedSource.availableQuantity})`);
      }
    });

    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateReallocation();
    if (validationErrors.length > 0) {
      dispatch(showAlert({
        message: validationErrors.join('\n'),
        type: 'warning'
      }));
      return;
    }

    setSubmitting(true);
    try {
      const validRows = transferRows.filter(row => {
        const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');
        const isShipmentOrigin = row.shipmentOrigin;
        return row.barcode.trim() &&
               row.searchResult &&
               row.selectedDestination &&
               (isGeneratedBox || isShipmentOrigin || row.selectedSource);
      });

      const unitTransfers: any[] = [];
      const boxTransfers: any[] = [];

      validRows.forEach(row => {
        const isGeneratedBox = row.barcode.startsWith('GEN-BOX-');
        const isShipmentOrigin = row.shipmentOrigin;
        const destinationStorageUnitId = row.selectedDestination!;
        const destinationBoxRef =
          row.selectedDestinationBoxRef &&
          row.selectedDestinationBoxRef.trim() !== '' &&
          row.selectedDestinationBoxRef !== 'independent'
            ? row.selectedDestinationBoxRef
            : null;

        if (isShipmentOrigin) {
          const shipmentBoxRef = row.shipmentBoxBarcode || row.barcode;
          const sourceStorageUnitId = row.selectedSource?.storageUnitId;

          if (sourceStorageUnitId) {
            boxTransfers.push({
              boxBarcode: shipmentBoxRef,
              sourceStorageUnitId,
              destinationStorageUnitId,
              destinationBoxRefNumber: destinationBoxRef
            });
          }
        } else if (isGeneratedBox) {
          const boxItems = row.searchResult?.unit?.boxItems || [];
          const boxReference = `BOX-${row.barcode.replace(/^GEN-/, '')}`;

          if (boxItems.length > 0) {
            boxItems.forEach((item: BoxItem) => {
              const itemSourceStorageUnitId = item.selectedSource?.storageUnitId || null;
              const rawBoxBarcode = item.selectedSource?.boxBarcode;
              const itemSourceBoxRefNumber = rawBoxBarcode ?
                normalizeBox(rawBoxBarcode) : null;

              const finalDestinationBoxRef = destinationBoxRef || boxReference;

              unitTransfers.push({
                unitId: item.unitId,
                quantity: item.quantity,
                sourceStorageUnitId: itemSourceStorageUnitId,
                destinationStorageUnitId,
                sourceBoxRefNumber: itemSourceBoxRefNumber,
                destinationBoxRefNumber: finalDestinationBoxRef,
                isGenerated: true
              });
            });
          } else {
            const cleanBarcode = row.barcode.replace(/^GEN-BOX-/, '');
            const sourceStorageUnitId = row.selectedSource?.storageUnitId || null;
            const sourceBoxRefNumber = row.selectedSource?.boxBarcode ?
              normalizeBox(row.selectedSource.boxBarcode) : null;

            const finalDestinationBoxRef = destinationBoxRef || boxReference;

            unitTransfers.push({
              unitId: cleanBarcode,
              quantity: row.quantity,
              sourceStorageUnitId,
              destinationStorageUnitId,
              sourceBoxRefNumber,
              destinationBoxRefNumber: finalDestinationBoxRef,
              isGenerated: true
            });
          }
        } else {
          const sourceStorageUnitId = row.selectedSource!.storageUnitId;
          const sourceBoxBarcode = normalizeBox(row.selectedSource!.boxBarcode);

          if (row.searchResult!.type === 'box' && sourceBoxBarcode) {
            boxTransfers.push({
              boxBarcode: sourceBoxBarcode,
              sourceStorageUnitId,
              destinationStorageUnitId,
              destinationBoxRefNumber: destinationBoxRef
            });
          } else {
            unitTransfers.push({
              unitId: row.searchResult!.unitId || row.barcode,
              quantity: row.quantity,
              sourceStorageUnitId,
              destinationStorageUnitId,
              sourceBoxRefNumber: normalizeBox(row.selectedSource!.boxBarcode) || null,
              destinationBoxRefNumber: destinationBoxRef
            });
          }
        }
      });

      const payload = {
        unitTransfers,
        boxTransfers,
        name: reallocationData.name,
        refNumber: reallocationData.refNumber,
        notes: reallocationData.notes,
        referenceId: reallocationData.referenceId,
        operationDate: reallocationData.date ? `${reallocationData.date}T00:00:00` : undefined
      };

      const response = await DataService.post('api/operation/reallocation', payload);

      if (!response.ok) {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Failed to submit reallocation');
      }

      const result = await response.json();

      dispatch(showAlert({
        message: `Successfully processed reallocation: ${reallocationData.name}`,
        type: 'success'
      }));

      clearPersistentState();

      navigate('/movements/reallocation');
    } catch (error: any) {
      dispatch(showAlert({
        message: error.message || 'Failed to submit reallocation',
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = reallocationData.name.trim() !== '' && statusCounts.readyToMove > 0;

  // Cancel handler with form reset
  const handleCancel = () => {
    clearPersistentState();
    navigate('/movements/reallocation');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Paper sx={{ borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/movements/reallocation')} aria-label="ArrowBack">
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  New Reallocation
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Create a new reallocation between storage units
                  </Typography>
                  {persistentState.lastUpdated > 0 && (
                    <Chip
                      label={`Auto-saved: ${new Date(persistentState.lastUpdated).toLocaleTimeString()}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || !canSubmit}
                startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
              >
                {submitting ? 'Processing...' : 'Execute Reallocation'}
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                label="Date *"
                type="date"
                value={reallocationData.date}
                onChange={(e) => setReallocationData(prev => ({ ...prev, date: e.target.value }))}
                fullWidth
                required
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Operation Name *"
                value={reallocationData.name}
                onChange={(e) => setReallocationData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Quarterly Reallocation"
                fullWidth
                required
                error={!reallocationData.name.trim()}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Reference Number"
                value={reallocationData.refNumber}
                onChange={(e) => setReallocationData(prev => ({ ...prev, refNumber: e.target.value }))}
                placeholder="REALLOC-001"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label="Notes"
                value={reallocationData.notes}
                onChange={(e) => setReallocationData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes"
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </Box>

      </Paper>

      <Box sx={{ flex: 1, p: 3 }}>
        <Card variant="outlined">
          <CardContent sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Actions:
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setShipmentDialogOpen(true);
                    loadAllShipments();
                  }}
                  startIcon={<LocalShipping />}
                  color="primary"
                  disabled={submitting}
                >
                  Import Shipment
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setImportDialogOpen(true)}
                  startIcon={<Upload />}
                  disabled={submitting}
                >
                  Import Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setGenerateBoxesDialog(true)}
                  startIcon={<AddBoxOutlined />}
                  disabled={submitting || generatingBoxes}
                >
                  {generatingBoxes ? 'Generating...' : 'Generate Boxes'}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSmartAllocate}
                  disabled={submitting || smartAllocateLoading || statusCounts.withBarcodes === 0 || statusCounts.shipmentOrigin === statusCounts.withBarcodes}
                  color="secondary"
                  startIcon={smartAllocateLoading ? <CircularProgress size={16} /> : <Autorenew />}
                >
                  {smartAllocateLoading ? 'Analyzing...' : `Smart Allocate (${statusCounts.withBarcodes - statusCounts.shipmentOrigin})`}
                </Button>
                {statusCounts.missingSources > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAllWithNoSource}
                    disabled={submitting}
                  >
                    Select Missing Sources
                  </Button>
                )}
                {statusCounts.missingDestinations > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAllWithNoDestination}
                    disabled={submitting}
                  >
                    Select Missing Destinations
                  </Button>
                )}
                {selectedRows.size > 0 && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setBulkSourceDialogOpen(true)}
                      disabled={submitting}
                      color="info"
                    >
                      Bulk Source
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setBulkDestinationDialogOpen(true)}
                      disabled={submitting}
                      color="info"
                    >
                      Bulk Destination
                    </Button>
                  </>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {statusCounts.shipmentOrigin > 0 && (
                  <Chip
                    label={`${statusCounts.shipmentOrigin} from Shipment`}
                    size="small"
                    color="primary"
                    variant="filled"
                    icon={<LocalShipping />}
                  />
                )}
                <Chip
                  label={`${selectedRows.size} Selected`}
                  size="small"
                  color={selectedRows.size > 0 ? "primary" : "default"}
                  variant="outlined"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {renderItemsToTransfer()}
      </Box>

      <Dialog 
        open={shipmentDialogOpen} 
        onClose={() => {
          if (!loadingShipmentAnalysis) {
            setShipmentDialogOpen(false);
            setSelectedShipment(null);
            setShipmentSearchText('');
            setShipmentOptions([]);
            setAllShipments([]);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalShipping />
            <Typography variant="h6">Import Shipment for Reallocation</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Search for a shipment to import its boxes for reallocation. The system will analyze the shipment and provide AI-powered destination recommendations.
            </Typography>
            <Autocomplete
              options={shipmentOptions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.refNumber}
              loading={loadingShipments}
              value={selectedShipment}
              onChange={(_, newValue) => setSelectedShipment(typeof newValue === 'string' ? null : newValue)}
              inputValue={shipmentSearchText}
              onInputChange={(_, newInputValue) => {
                setShipmentSearchText(newInputValue);
                filterShipments(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Shipment"
                  placeholder="Enter shipment reference or ID"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingShipments && <CircularProgress size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {typeof option === 'string' ? option : option.refNumber}
                    </Typography>
                    {typeof option !== 'string' && (
                      <Typography variant="caption" color="text.secondary">
                        ID: {option.id}
                      </Typography>
                    )}
                  </Stack>
                </li>
              )}
            />
            {selectedShipment && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle2" color="primary">Selected Shipment:</Typography>
                  <Typography variant="body2"><strong>Reference:</strong> {selectedShipment.refNumber}</Typography>
                  <Typography variant="body2"><strong>ID:</strong> {selectedShipment.id}</Typography>
                </Stack>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShipmentDialogOpen(false);
              setSelectedShipment(null);
              setShipmentSearchText('');
              setShipmentOptions([]);
              setAllShipments([]);
            }}
            disabled={loadingShipmentAnalysis}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selectedShipment && handleLoadShipment(selectedShipment)}
            variant="contained"
            disabled={!selectedShipment || loadingShipmentAnalysis}
            startIcon={loadingShipmentAnalysis ? <CircularProgress size={20} /> : <LocalShipping />}
          >
            {loadingShipmentAnalysis ? 'Analyzing Shipment...' : 'Import Shipment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Destination Dialog */}
      <Dialog open={bulkDestinationDialogOpen} onClose={() => setBulkDestinationDialogOpen(false)}>
        <DialogTitle>Apply Bulk Destination</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Destination Storage Unit</InputLabel>
            <Select
              value={selectedBulkDestination}
              onChange={(e) => setSelectedBulkDestination(e.target.value)}
              label="Destination Storage Unit"
            >
              {Object.entries(destinationUnits).map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Storage fontSize="small" />
                    <Typography>{name}</Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkDestinationDialogOpen(false);
            setSelectedBulkDestination('');
          }}>Cancel</Button>
          <Button
            onClick={() => {
              handleBulkDestinationApply(selectedBulkDestination);
              setBulkDestinationDialogOpen(false);
              setSelectedBulkDestination('');
            }}
            variant="contained"
            disabled={!selectedBulkDestination}
          >
            Apply to {selectedRows.size} Items
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Source Dialog */}
      <Dialog open={bulkSourceDialogOpen} onClose={() => setBulkSourceDialogOpen(false)}>
        <DialogTitle>Apply Bulk Source</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Source Storage Unit</InputLabel>
            <Select
              value={selectedBulkSource}
              onChange={(e) => setSelectedBulkSource(e.target.value)}
              label="Source Storage Unit"
            >
              {Array.from(new Set(
                transferRows
                  .filter(row => row.searchResult?.locations && row.searchResult.locations.length > 0)
                  .flatMap(row => row.searchResult!.locations.map(loc => loc.storageUnitId))
              )).map(storageUnitId => {
                const location = transferRows
                  .find(row => row.searchResult?.locations?.some(loc => loc.storageUnitId === storageUnitId))
                  ?.searchResult?.locations?.find(loc => loc.storageUnitId === storageUnitId);

                return location ? (
                  <MenuItem key={storageUnitId} value={storageUnitId}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Storage fontSize="small" />
                      <Typography>{location.storageUnitName}</Typography>
                    </Stack>
                  </MenuItem>
                ) : null;
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBulkSourceDialogOpen(false);
            setSelectedBulkSource('');
          }}>Cancel</Button>
          <Button
            onClick={() => {
              handleBulkSourceApply(selectedBulkSource);
              setBulkSourceDialogOpen(false);
              setSelectedBulkSource('');
            }}
            variant="contained"
            disabled={!selectedBulkSource}
          >
            Apply to {selectedRows.size} Items
          </Button>
        </DialogActions>
      </Dialog>

      <GenerateBoxesDialog
        open={generateBoxesDialog}
        onClose={() => setGenerateBoxesDialog(false)}
        onGenerate={(count: number) => {
          setGeneratingBoxes(true);

          try {
            const generatedBoxes: TransferRow[] = [];
            const timestamp = Date.now();

            for (let i = 1; i <= count; i++) {
              const boxBarcode = `GEN-BOX-${timestamp}-${String(i).padStart(3, '0')}`;

              generatedBoxes.push({
                id: `gen-${timestamp}-${i}`,
                barcode: boxBarcode,
                searchResult: {
                  type: 'box',
                  locations: [],
                  itemName: `Generated Box ${i}`,
                  unitId: boxBarcode,
                  unit: null
                } as BarcodeSearchResult,
                quantity: 1,
                loading: false,
                selectedSource: undefined,
                selectedDestination: undefined
              });
            }

            setTransferRows(prev => {
              const existing = prev.filter(x => x.barcode.trim());
              return [
                ...existing,
                ...generatedBoxes,
                { id: Date.now().toString(), barcode: '', searchResult: null, quantity: 1, loading: false }
              ];
            });

            dispatch(showAlert({
              message: `Generated ${count} boxes for reallocation. Click on box names to add items.`,
              type: 'success'
            }));

          } catch (error: any) {
            dispatch(showAlert({
              message: error.message || 'Failed to generate boxes',
              type: 'error'
            }));
          } finally {
            setGeneratingBoxes(false);
          }

          setGenerateBoxesDialog(false);
        }}
        loading={generatingBoxes}
        title="Generate Boxes for Reallocation"
        description="Generate new boxes for reallocation. Items can be scanned into these boxes and then moved to different storage locations."
      />

      <EnhancedBoxScanDialog
        open={boxScanDialog.open}
        onClose={() => setBoxScanDialog({ open: false, rowId: '', boxBarcode: '', boxDetails: null })}
        boxBarcode={boxScanDialog.boxBarcode}
        boxDetails={boxScanDialog.boxDetails}
        onItemsConfirm={(items: BoxItem[]) => {
          const { rowId } = boxScanDialog;

          // Extract source information from the first item with source data
          const itemWithSource = items.find(item => item.selectedSource);
          const sourceInfo = itemWithSource?.selectedSource;

          setTransferRows(prev => prev.map(row =>
            row.id === rowId && row.searchResult
              ? {
                  ...row,
                  searchResult: {
                    ...row.searchResult,
                    unit: { ...row.searchResult?.unit, boxItems: items }
                  } as BarcodeSearchResult,
                  selectedSource: sourceInfo ? {
                    storageUnitId: sourceInfo.storageUnitId,
                    storageUnitName: sourceInfo.storageUnitName,
                    boxBarcode: sourceInfo.boxBarcode,
                    availableQuantity: sourceInfo.availableQuantity,
                    availableBoxes: {}
                  } : row.selectedSource
                }
              : row
          ));
          
          dispatch(showAlert({
            message: `Updated box ${boxScanDialog.boxBarcode} with ${items.length} items`,
            type: 'success'
          }));
          
          setBoxScanDialog({ open: false, rowId: '', boxBarcode: '', boxDetails: null });
        }}
        context="reallocation"
        storageUnits={destinationUnits}
      />

      <ExcelImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImport={handleImport}
        title="Import Items for Reallocation"
        expectedColumns={{
          required: ['barcode', 'quantity'],
          optional: ['source', 'destination', 'boxBarcode']
        }}
      />
    </Box>
  );
};

export default NewReallocationPage;